import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Package, Activity, Shield, ArrowRight, Eye, EyeOff } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import { useSystemLogo } from '../hooks/useSystemLogo';
import logologmedDefault from '../assets/logo-logmed.png';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  const login = useAuthStore((state) => state.login);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const deveTrocarSenha = useAuthStore((state) => state.deveTrocarSenha);

  const { logoUrl, nomeSistema } = useSystemLogo();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await login(email, password, rememberMe);
      // Verifica se precisa trocar senha antes de acessar o sistema
      const store = useAuthStore.getState();
      if (store.deveTrocarSenha) {
        navigate('/trocar-senha');
      } else {
        navigate('/cloud/calendario');
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const features = [
    {
      icon: Package,
      title: 'Gestão de Inventário',
      description: 'Controle completo de produtos consignados em tempo real'
    },
    {
      icon: Building2,
      title: 'Multi-hospitalar',
      description: 'Gerencie múltiplas unidades hospitalares de forma centralizada'
    },
    {
      icon: Activity,
      title: 'Contagens em Tempo Real',
      description: 'Acompanhe as contagens físicas com sincronização automática'
    },
    {
      icon: Shield,
      title: 'Segurança e Auditoria',
      description: 'Rastreabilidade completa e conformidade regulatória'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo e Header */}
        <div className="text-center mb-8">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={nomeSistema}
              className="h-12 mx-auto mb-6"
              onError={(e) => { e.currentTarget.src = logologmedDefault; }}
            />
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {nomeSistema}
          </h1>
          <p className="text-gray-600">
            Acesse o sistema com suas credenciais
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Login</h2>

          {/* Mensagem de Erro */}
          {error && (
            <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-900">⚠️ Erro ao fazer login</p>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu.email@hospital.com.br"
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Senha
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded cursor-pointer"
                />
                <span className="text-sm text-gray-600">Lembrar de mim</span>
              </label>
              <a href="#" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                Esqueceu a senha?
              </a>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                'Entrando...'
              ) : (
                <>
                  Entrar no Sistema
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

        </div>

        {/* Footer */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            {/* Copyright - esquerda */}
            <p className="text-[10px] text-gray-400 ml-12">
              © 2025 AuditLog. Todos os direitos reservados.
            </p>

            {/* Desenvolvido por - direita (texto acima do logo) */}
            <div className="flex flex-col items-end">
              <span className="text-[10px] text-gray-700 dark:text-gray-300 mb-1">Desenvolvido por:</span>
              <img
                src="/logo_hyperfocus.png"
                alt="Hyperfocus"
                className="h-12 object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
